import React from "react";

import { gql } from "@apollo/client";
import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type GetSubLevelBreakdown,
  type GetSubLevelBreakdownVariables,
} from "@north-beam/nb-client-side";
import { makeOverviewRows } from "@north-beam/nb-client-side";
import {
  type AdKey,
  deslugify,
  isCampaign,
  todayLocalAsISODate,
  Granularity,
  type IGranularity,
  type IMCAttributionWindowDays,
} from "@north-beam/nb-common";
import { TableSection } from "@pages/sales/table-section";
import { GranularitySelect } from "@shared/selects";
import { type GranularitySelectOption } from "@shared/selects/granularity-select";
import { CommonGQLFragments } from "@utils/common-gql-fragments";

import {
  type ReportGQLVariables,
  type ReportState,
  reportStateToGQLVariables,
} from "./report-state";

export const GET_SUB_LEVEL_BREAKDOWN = gql`
  query GetSubLevelBreakdown(
    $id: ID!
    $dateRange: JSONObject!
    $compareDateRange: JSONObject!
    $attributionMethod: String!
    $attributionWindows: [String!]!
    $nbAccountingMode: String!
    $granularity: String!
  ) {
    me {
      id
      adObject(id: $id) {
        id
        subLevelBreakdown(
          dateRange: $dateRange
          compareDateRange: $compareDateRange
          attributionMethod: $attributionMethod
          attributionWindows: $attributionWindows
          nbAccountingMode: $nbAccountingMode
          granularity: $granularity
        ) {
          endDate
          startDate
          rows {
            ...SalesReportRowAll
          }
          hasV1Forecasts
          customGoals {
            id
            displayName
          }
        }
      }
    }
  }
  ${CommonGQLFragments.salesReportRowAll}
`;

interface SubLevelBreakdownPageProps {
  id: string;
  state: ReportState;
}

export function SubLevelBreakdownPage({
  id,
  state,
}: SubLevelBreakdownPageProps) {
  const anchor = todayLocalAsISODate();
  const gqlVariables = React.useMemo(() => {
    return reportStateToGQLVariables(state, anchor);
  }, [state, anchor]);

  const {
    dateRange,
    compareDateRange,
    attributionWindows,
    attributionMethod,
    nbAccountingMode,
  } = gqlVariables;

  const adKey = React.useMemo(() => {
    return deslugify(id);
  }, [id]);

  const granularityChoices = React.useMemo(() => {
    return getGranularityChoices(adKey);
  }, [adKey]);

  const [granularity, setGranularity] = React.useState(granularityChoices[0]);

  const { data, loading } = useNorthbeamQuery<
    GetSubLevelBreakdown,
    GetSubLevelBreakdownVariables
  >(GET_SUB_LEVEL_BREAKDOWN, {
    useHeavy: true,
    variables: {
      id,
      granularity,
      dateRange,
      compareDateRange,
      attributionWindows,
      attributionMethod,
      nbAccountingMode,
    },
  });

  let inner: React.ReactElement;

  if (loading) {
    inner = <LoadingSlide />;
  } else {
    const subLevelBreakdown = data?.me.adObject?.subLevelBreakdown;
    if (!subLevelBreakdown || subLevelBreakdown.rows.length === 0) {
      inner = <span>No data for this time range.</span>;
    } else {
      inner = (
        <Inner
          response={subLevelBreakdown}
          gqlVariables={gqlVariables}
          granularity={granularity}
        />
      );
    }
  }

  return (
    <>
      <GranularitySelect
        isLoading={loading}
        /* This type coercion was done for purposes of not disrupting functionality when
           solidifying the types for the GranularitySelect.  Previously, this component did not
           specify 'hasPlatform', but the GranularitySelect's default for 'hasPlatform' is
           false.  This means that the granularity it accepts, and the granularity it includes
           via 'onChange', excludes "platform".  However, the typings in this component are
           allowing the granularity to include "platform" - which is incorrect.  For now,
           however, we simply coerce the type to exclude "platform" to avoid breaking changes. */
        granularity={granularity as Exclude<IGranularity, "platform">}
        hasPlatform={false}
        onChange={setGranularity}
        options={
          Granularity.reactSelectOptions.filter(
            (v) => granularityChoices.includes(v.value),
            /* This type coercion is also done for the same purposes as listed above.  It should be
               fixed. */
          ) as GranularitySelectOption<false>[]
        }
      />
      {inner}
    </>
  );
}

function Inner({
  response,
  granularity,
  gqlVariables,
}: {
  response: any;
  granularity: IGranularity;
  gqlVariables: ReportGQLVariables;
}) {
  const {
    attributionMethod,
    attributionWindows,
    compareDateRange,
    dateRange,
    nbAccountingMode,
    timeGranularity,
  } = gqlVariables;

  const { user } = useUser();

  const metricsArray = React.useMemo(
    () =>
      makeOverviewRows(
        attributionMethod,
        attributionWindows[0] as IMCAttributionWindowDays,
        false,
        nbAccountingMode,
        response.customGoals,
        timeGranularity as any,
        user,
      ),
    [
      response,
      attributionMethod,
      attributionWindows,
      nbAccountingMode,
      timeGranularity,
      user,
    ],
  );

  return (
    <TableSection
      rows={response.rows}
      breakdownConfigs={[]}
      dateRange={dateRange}
      compareDateRange={compareDateRange}
      breakdowns={[]}
      granularity={granularity}
      metricsArray={metricsArray}
    />
  );
}

function getGranularityChoices(adKey: AdKey): IGranularity[] {
  if (isCampaign(adKey)) {
    return ["adset", "ad"];
  }
  return ["ad"];
}
